import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Clock, ArrowRight } from 'lucide-react';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const result = await login(email, password);
            // Check if MFA is required
            if (result?.mfa_required) {
                navigate('/mfa-verify', { state: { email, tempToken: result.temp_token } });
                return;
            }
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col">
            {/* Hero Section */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo */}
                <div className="flex flex-col items-center mb-10">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw Logo" 
                        className="h-32 w-32 object-contain"
                    />
                    <h1 className="font-brand text-3xl text-foreground mt-4">Voilaw</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Time & task tracking for attorneys
                    </p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-foreground">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="attorney@lawfirm.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="login-email-input"
                            className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-foreground">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="login-password-input"
                            className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                        />
                    </div>
                    
                    {/* Forgot password link */}
                    <div className="text-right">
                        <Link
                            to="/forgot-password"
                            className="text-sm text-[#1C6EE7] hover:underline"
                            data-testid="forgot-password-link"
                        >
                            Forgot password?
                        </Link>
                    </div>

                    <div className="pt-4">
                        <Button
                            type="submit"
                            className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7] shadow-lg hover:shadow-xl transition-all"
                            disabled={isLoading}
                            data-testid="login-submit-btn"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Clock className="h-5 w-5 animate-spin" />
                                    Signing in...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    Sign in
                                    <ArrowRight className="h-5 w-5" />
                                </span>
                            )}
                        </Button>
                    </div>
                </form>

                <div className="mt-8 text-center">
                    <p className="text-muted-foreground">
                        Don&apos;t have an account?{' '}
                        <Link
                            to="/register"
                            className="text-primary font-semibold hover:underline"
                            data-testid="register-link"
                        >
                            Create one
                        </Link>
                    </p>
                </div>
                
                {/* Footer with Terms Link */}
                <div className="mt-8 pt-6 border-t border-slate-200/50 w-full max-w-sm">
                    <p className="text-xs text-center text-slate-400">
                        By signing in, you agree to our{' '}
                        <Link to="/terms" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                            Terms of Use
                        </Link>
                        {' '}and{' '}
                        <Link to="/privacy" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                            Privacy Policy
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
